import React from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight } from "lucide-react";

/**
 * A polished lock overlay teaser for Pro-only features.
 * Usage: wrap the gated content and set `locked` to true for free users.
 *
 * <ProLock
 *   locked={!isPro}
 *   title="Credit optimizer"
 *   description="Pay-down suggestions that keep every card under 30% utilization."
 *   onUpgrade={() => setUpgradeOpen(true)}
 * >
 *   {yourGatedContent}
 * </ProLock>
 */
export const ProLock = ({
  locked = false,
  title = "Pro feature",
  description = "Unlock this with CardWatch Pro.",
  bullets = [],
  onUpgrade,
  upgradeHref = null,
  children,
  testid = "pro-lock",
}) => {
  if (!locked) return children;

  const CTA = onUpgrade ? (
    <button
      type="button"
      onClick={onUpgrade}
      data-testid={`${testid}-upgrade`}
      className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-5 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)]"
    >
      Try Pro free <ArrowRight size={13} />
    </button>
  ) : (
    <Link
      to={upgradeHref || "/dashboard"}
      data-testid={`${testid}-upgrade`}
      className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-5 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)]"
    >
      Try Pro free <ArrowRight size={13} />
    </Link>
  );

  return (
    <div className="relative min-h-[440px] sm:min-h-[380px]" data-testid={testid}>
      {/* Blurred preview of the underlying content */}
      <div
        className="pointer-events-none select-none opacity-[0.35] blur-[3px] h-full"
        aria-hidden="true"
      >
        {children}
      </div>

      {/* Glass panel overlay */}
      <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6">
        <div
          className="w-full max-w-md rounded-2xl border border-[#00E676]/30 bg-[#060B19]/85 backdrop-blur-xl p-5 sm:p-7 text-center shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
          data-testid={`${testid}-panel`}
        >
          <div className="mx-auto w-11 h-11 rounded-2xl bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center">
            <Lock size={18} className="text-[#00E676]" />
          </div>
          <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
            <Sparkles size={11} /> Pro feature
          </div>
          <h3
            className="mt-2 text-white text-lg sm:text-xl font-semibold tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {title}
          </h3>
          <p className="mt-2 text-sm text-slate-300 leading-relaxed">{description}</p>
          {bullets.length > 0 && (
            <ul className="mt-4 text-left space-y-2 text-xs text-slate-300 max-w-xs mx-auto">
              {bullets.map((b, i) => {
                const colonIdx = typeof b === "string" ? b.indexOf(":") : -1;
                const hasTitle = colonIdx > 0 && colonIdx < 40;
                const title = hasTitle ? b.slice(0, colonIdx) : null;
                const rest = hasTitle ? b.slice(colonIdx + 1).trim() : b;
                return (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1 inline-block w-1 h-1 rounded-full bg-[#00E676] flex-shrink-0" />
                    <span>
                      {hasTitle && (
                        <span className="text-white font-semibold">{title}: </span>
                      )}
                      {rest}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-5 flex justify-center">{CTA}</div>
          <p className="mt-3 text-[11px] text-slate-300">7 days free · no card required</p>
        </div>
      </div>
    </div>
  );
};

export default ProLock;
